import { Avatar, Card, Input, notification, Skeleton, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { sanitize } from 'dompurify';
import DebouncedButton from '../../components/Button/Button';
import BackgroundTop from '../../components/BackgroundTop/BackgroundTop';
import { t } from '../../i18n/i18n';
import {
  getAllIntegratedFeatures,
  joinIntegrationForWorkspace,
} from '../../API/services/alkymersService';
import './Integration.scss';
import ModalCustom from '../../components/ModalCustom/ModalCustom';
import CheckIcon from '../../assets/login/CheckIcon';
import { CustomFeatureEnum } from '../../constants/enums';
import useTranslations from '../../hooks/useTranslations';

const { Meta } = Card;

export default function IntegrationDashboard() {
  const [integrationList, setIntegrationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [preload, setPreload] = useState(false);
  const { user, currentWorkspace } = useSelector((state) => state.auth);
  const [integrationData, setIntegrationData] = useState({});
  const { key } = useTranslations();
  const disableJoin =
    selectedIntegration?.featureEnum === CustomFeatureEnum.HIRING_ROOM &&
    (!integrationData?.client_id || !integrationData?.client_secret);

  const integrationHRTokenExpired =
    new Date(
      user?.companyCustomFeatures?.customFeatures?.find(
        (i) => i.featureEnum === CustomFeatureEnum.HIRING_ROOM
      )?.authentication?.expiresAt
    ) < new Date() || false;

  const handleJoinClick = (integration) => {
    setSelectedIntegration(integration);
    setShowConfirmModal(true);
  };

  const handleClearData = () => {
    setIntegrationData({});
  };

  const handleIntegrationData = (key, value) => {
    const copyData = {
      ...integrationData,
      [key]: value,
    };
    setIntegrationData(copyData);
  };

  const handleConfirmJoin = async (isRefresh = false) => {
    setPreload(true);
    try {
      const { url } = await joinIntegrationForWorkspace({
        ...integrationData,
        feature: selectedIntegration.featureEnum,
      });
      if (url) {
        window.location.href = url;
      }
      if (isRefresh) {
        notification.open({
          message: t('SUCCESS'),
          type: 'success',
          description: t('TOKEN_REFRESH_INITIATED'),
        });
      }
    } catch (e) {
      console.error(e);
      notification.open({
        message: t('ERROR'),
        type: 'error',
        description: t(e?.response?.data?.message),
      });
    } finally {
      setPreload(false);
      handleClearData();
      setShowConfirmModal(false);
      setSelectedIntegration(null);
    }
  };

  const getIntegrationDataModal = () => {
    //this allows to handle multiple data needed for future integrations like apiKeys, user credentials, etc
    switch (selectedIntegration?.featureEnum) {
      case CustomFeatureEnum.HIRING_ROOM:
        return (
          <>
            <div className="m-1" style={{ textAlign: 'justify' }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitize(t('HIRING_ROOM_INTEGRATION_TERMS')),
                }}
              />
            </div>
            <Input
              placeholder={t('CLIENT_ID_PLACEHOLDER')}
              className="m-2"
              prefix={<UserOutlined />}
              name="client_id"
              value={integrationData?.client_id}
              onChange={(e) => handleIntegrationData(e.target.name, e.target.value)}
              autoComplete="off"
            />
            <Input.Password
              placeholder={t('CLIENT_SECRET_PLACEHOLDER')}
              className="m-2"
              prefix={<LockOutlined />}
              name="client_secret"
              value={integrationData?.client_secret}
              onChange={(e) => handleIntegrationData(e.target.name, e.target.value)}
              autoComplete="new-password"
            />
          </>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    const getIntegrations = async () => {
      try {
        setLoading(true);
        const { collection } = await getAllIntegratedFeatures(key);
        const userIntegrated = user?.companyCustomFeatures?.customFeatures;
        const labeled =
          collection?.map((f) => ({
            ...f,
            alreadyJoined:
              userIntegrated?.some((ui) => ui.featureEnum === f.featureEnum) &&
              user.companyCustomFeatures?.workspaceId === currentWorkspace,
          })) ?? [];

        setIntegrationList(labeled);
      } catch (e) {
        notification.error({
          message: t('Error'),
          type: 'error',
        });
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    getIntegrations();
  }, [currentWorkspace, user?.companyCustomFeatures]);

  return (
    <div className="home-container mt-5">
      <div>
        <BackgroundTop
          title={t('INTEGRATIONS')}
          showBase
          className="position-relative"
          titleClassname="text-title"
        />
      </div>
      <ModalCustom
        onCancel={() => {
          setShowConfirmModal(false);
          handleClearData();
        }}
        onCancelText={t('CANCEL')}
        onOk={handleConfirmJoin}
        classModal="allow-certificate-download-modal-width"
        subtitleClassName="px-0 px-lg-0"
        setShow={setShowConfirmModal}
        onOkText={t('CONFIRM_MODAL_CONFIRM')}
        title={t('CONNECT_INTEGRATION_QUESTION')}
        show={showConfirmModal}
        confirmButtonText={t('CONFIRM_MODAL_CONFIRM')}
        showCross
        isLoading={preload}
        subtitle={getIntegrationDataModal()}
        onOkDisabled={disableJoin}
      />
      <div className="position-relative description-container px-2 d-flex mx-auto justify-content-between flex-wrap">
        <div className="my-2">
          <h1 className="text-3xl font-bold tracking-tight">{t('INTEGRATIONS_TITLE')}</h1>
          <p className="text-muted-foreground mt-2">{t('INTEGRATIONS_SUBTITLE')}</p>
        </div>
        <div className="d-flex bg-background mx-4 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 overflow-auto">
            {loading ? (
              <>
                <div className="d-flex my-2 p-3 integration-card bg-white">
                  <Skeleton loading active avatar paragraph={{ rows: 3 }} />
                </div>
                <div className="d-flex my-2 p-3 integration-card bg-white">
                  <Skeleton loading active avatar paragraph={{ rows: 3 }} />
                </div>
              </>
            ) : (
              integrationList.map((integration) => (
                <Card key={integration.featureEnum} className="d-flex my-2 integration-card">
                  <Meta
                    avatar={<Avatar src={integration.iconUrl} />}
                    title={integration?.title}
                    description={
                      <>
                        <div className="flex items-center">
                          {integration.description ?? <>&nbsp;</>}
                        </div>
                        <div className="d-flex justify-content-end mt-2">
                          <DebouncedButton
                            size="sm"
                            className={`button rounded-0 ${
                              integration.joinable && !integration.alreadyJoined
                                ? 'bg-blue-principal text-white'
                                : 'text-blue-principal'
                            }`}
                            onClick={() =>
                              integration?.joinable
                                ? handleJoinClick(integration)
                                : window.open(
                                    process.env.RAZZLE_RUNTIME_SALES_CALENDAR_URL,
                                    '_blank',
                                    'noopener,noreferrer'
                                  )
                            }
                            disabled={
                              loading || (!integrationHRTokenExpired && integration?.alreadyJoined)
                            }
                          >
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip className="">
                                  {integration?.alreadyJoined ? t('ALREADY_JOINED') : ''}
                                </Tooltip>
                              }
                            >
                              <>
                                {integration?.alreadyJoined &&
                                integration?.featureEnum === CustomFeatureEnum.HIRING_ROOM &&
                                integrationHRTokenExpired ? (
                                  t('REFRESH_TOKEN')
                                ) : integration?.alreadyJoined ? (
                                  <CheckIcon className="mx-2" />
                                ) : integration?.joinable ? (
                                  t('CONNECT')
                                ) : (
                                  t('MORE_INFO')
                                )}
                              </>
                            </OverlayTrigger>
                          </DebouncedButton>
                        </div>
                      </>
                    }
                  />
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
